// Lib
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch } from 'redux';

// Actions
import { ClosePopup, OpenPopup } from '@actions/popup';
import { UserAdd } from '@actions/user';

// Components
import SlideModal from '@components/SlideModal';

// Hooks
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

// Graph
import { getAccountByNameQuery } from '@graphql/queries/account';

// Utils
import getLinkWithUTMParams from '@utils/getLinkWithUTMParams';

interface AdvancedColor {
  isActive?: boolean;
  background?: string;
  primary?: string;
  secondary?: string;
  text?: string;
  icons?: string;
  shadow1?: string;
  shadow3?: string;
}

interface BackgroundSettings {
  colorMode?: string;
  advancedColor?: AdvancedColor;
}

interface ShareableSettings {
  avatar?: { url?: string };
  fullName?: string;
}

interface Link {
  type?: string;
  postId?: string;
  url?: string;
}

interface ContactDetails {
  enabled?: boolean;
  infoMessage?: string;
  buttonText?: string;
}

interface Account {
  _id: string;
  username?: string;
  marbleId?: string;
  tagline?: string;
  links?: Link[];
  backgroundSettings?: BackgroundSettings & {
    headerImageUrl?: string;
    avatarAlignmentType?: 'center' | 'left';
    slideshowHidden?: boolean;
  };
  shareableSettings?: ShareableSettings;
  contactDetails?: ContactDetails;
  auths?: Array<{ _id: string; [key: string]: unknown }>;
}

interface LinkPreviewData {
  fullName?: string;
  username?: string;
  tagline?: string;
  previewImageUrl?: string;
}

interface UseCustomDomainProfileReturn {
  user: Account | undefined;
  loading: boolean;
  onOpenSlide: (index: number) => void;
  linkPreviewData: LinkPreviewData | undefined;
}

const useCustomDomainProfile = (
  host?: string,
): UseCustomDomainProfileReturn => {
  const domain = typeof window === 'object' ? window?.location?.hostname : host;
  const dispatch: Dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { pathname } = useLocation();

  // SSR works only with apollo
  const { data, error } = useQuery(getAccountByNameQuery, {
    variables: { input: { domain } },
    fetchPolicy: 'no-cache',
    skip: !domain,
  });

  useEffect(() => {
    if (data && data?.getAccount) {
      dispatch(UserAdd({ ...data?.getAccount }));
      setIsLoading(false);
    }
  }, [data, dispatch, setIsLoading]);

  useEffect(() => {
    if (error) {
      window.open('https://www.getmrbl.ai', '_self');
    }
  }, [error]);

  const onOpenSlide = useCallback(
    (index: number) => {
      const links = data?.getAccount?.links?.filter(
        ({ type }: Link) => type === 'slide',
      );

      const advancedColor = data?.getAccount?.backgroundSettings?.advancedColor;

      const marbleId = data?.getAccount?.marbleId;

      if (
        links[index]?.postId ||
        (!links[index]?.postId && !links[index]?.url)
      ) {
        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
          event: 'slide_click',
        });

        dispatch(
          OpenPopup({
            title: null,
            children: (
              <SlideModal
                _advancedColor={advancedColor}
                slide={links[index]}
                colorMode={data?.getAccount?.backgroundSettings.colorMode}
                avatarSrc={data?.getAccount?.shareableSettings?.avatar?.url}
                fullName={data?.getAccount?.shareableSettings?.fullName}
                pathname={pathname}
                username={pathname?.slice(1)}
                marbleId={marbleId}
              />
            ),
            onCancel: () => {
              dispatch(ClosePopup());
            },
            positiveText: null,
            negativeText: null,
            id: 'post-popup',
            style: {
              width: '450px',
              background: advancedColor?.isActive
                ? advancedColor?.background + ' !important'
                : null,
              boxShadow: advancedColor?.isActive
                ? `5px 5px 10px ${advancedColor?.shadow1}, inset 1px 1px 2px ${advancedColor?.shadow3}` +
                  ' !important'
                : null,
              '&&&&&& [fill]': {
                fill: advancedColor?.isActive ? advancedColor?.icons : null,
              },
              '&&&&&& span': {
                color: advancedColor?.isActive ? advancedColor?.primary : null,
              },
              '&&&&&& > div': {
                background: advancedColor?.isActive
                  ? advancedColor?.background + ' !important'
                  : null,
              },
              '&&&&&& > div:nth-child(2) img': {
                background: advancedColor?.isActive
                  ? advancedColor?.background
                  : null,
                boxShadow: advancedColor?.isActive
                  ? `5px 5px 10px ${advancedColor?.shadow1}, inset 1px 1px 2px ${advancedColor?.shadow3}`
                  : null,
              },
              '&&&&&& > div:nth-child(2) h3': {
                color: advancedColor?.isActive ? advancedColor?.text : null,
              },
              '&&&&&& > div:nth-child(2) p': {
                color: advancedColor?.isActive ? advancedColor?.icons : null,
              },
            },
            isMobileFullSize: false,
          }),
        );
      } else {
        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
          event: 'slide_click',
          url: links[index]?.url,

          // custom param
          custom_url: links[index]?.url,
        });

        window.open(
          getLinkWithUTMParams(links[index]?.url, pathname?.slice(1), marbleId),
          '_blank',
        );
      }
    },
    [dispatch, data?.getAccount, pathname],
  );

  const linkPreviewData = useMemo((): LinkPreviewData | undefined => {
    if (data) {
      const { shareableSettings, ...accountData } = data?.getAccount;
      return { fullName: shareableSettings.fullName, ...accountData };
    }

    return undefined;
  }, [data]);

  return {
    user: data?.getAccount,
    loading: isLoading,
    onOpenSlide,
    linkPreviewData,
  };
};

export default useCustomDomainProfile;
